import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { queryKeys } from "@/lib/queryKeys";
import { Button } from "@/components/Button";
import { Skeleton } from "@/components/Skeleton";
import { EmptyState } from "@/components/EmptyState";
import { MarkdownView } from "@/components/MarkdownView";
import { RichTextEditor } from "@/components/RichTextEditor";
import {
  DOC_KINDS,
  kindLabel,
  docTemplate,
  parseActionItems,
} from "@/lib/docTemplates";
import type { DocKind, DocumentRow } from "@shared/types";

function DocsView() {
  const { projectId } = Route.useParams();
  const { doc: docParam } = Route.useSearch();
  const qc = useQueryClient();
  const [selectedId, setSelectedId] = useState<string | null>(docParam ?? null);
  const [filter, setFilter] = useState<DocKind | "all">("all");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [ownerFilter, setOwnerFilter] = useState<string>("all");
  const [newKind, setNewKind] = useState<DocKind>("doc");
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");

  const list = useQuery({
    queryKey: queryKeys.documentsFor(projectId),
    queryFn: () => api.listDocuments(projectId),
  });
  const team = useQuery({ queryKey: queryKeys.team(), queryFn: api.listTeam });
  const memberName = (id: string) =>
    team.data?.members.find((m) => m.id === id)?.name ?? id.slice(0, 8);
  const docs = list.data?.documents ?? [];
  // Decision-log filters (status / owner) apply on top of the kind filter.
  const filtered = docs.filter((d) => {
    if (filter !== "all" && d.kind !== filter) return false;
    if (statusFilter !== "all" && (d.status ?? "") !== statusFilter)
      return false;
    if (ownerFilter !== "all" && d.createdBy !== ownerFilter) return false;
    return true;
  });
  const decisionOwners = Array.from(
    new Set(docs.filter((d) => d.kind === "decision").map((d) => d.createdBy)),
  );
  const selected = docs.find((d) => d.id === selectedId) ?? null;

  // Deep-link: when the ?doc= param changes (e.g. arriving from search),
  // select that document.
  useEffect(() => {
    if (docParam) setSelectedId(docParam);
  }, [docParam]);

  // Sync the editor fields whenever the selected doc changes.
  useEffect(() => {
    if (selected) {
      setTitle(selected.title);
      setBody(selected.body);
      setEditing(false);
    }
  }, [selectedId]);

  const invalidate = () =>
    qc.invalidateQueries({ queryKey: queryKeys.documentsFor(projectId) });

  const create = useMutation({
    mutationFn: () =>
      api.createDocument(projectId, {
        kind: newKind,
        title: `Untitled ${kindLabel(newKind)}`,
        body: docTemplate(newKind),
      }),
    onSuccess: (r) => {
      invalidate();
      setSelectedId(r.document.id);
    },
  });

  const save = useMutation({
    mutationFn: () =>
      api.updateDocument(selected!.id, { title: title.trim(), body }),
    onSuccess: () => {
      invalidate();
      setEditing(false);
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.deleteDocument(id),
    onSuccess: () => {
      invalidate();
      setSelectedId(null);
    },
  });

  // ADR lifecycle: update status or set the supersedes link.
  const adrPatch = useMutation({
    mutationFn: (v: {
      status?: "proposed" | "accepted" | "superseded";
      supersedesId?: string | null;
    }) => api.updateDocument(selected!.id, v),
    onSuccess: invalidate,
  });

  // Link/unlink the document to a task in this project.
  const tasksQ = useQuery({
    queryKey: queryKeys.tasksFor(projectId),
    queryFn: () => api.listTasks(projectId),
  });
  const linkTask = useMutation({
    mutationFn: (taskId: string | null) =>
      api.updateDocument(selected!.id, { taskId }),
    onSuccess: invalidate,
  });

  // Restore a prior snapshot: PATCH the doc back to that version's title+body
  // (which itself snapshots the current content, so a restore is reversible).
  const restore = useMutation({
    mutationFn: (v: { title: string; body: string }) =>
      api.updateDocument(selected!.id, { title: v.title, body: v.body }),
    onSuccess: (_d, v) => {
      setTitle(v.title);
      setBody(v.body);
      invalidate();
    },
  });

  // Export the selected document's Markdown source as a downloadable .md file.
  function exportMarkdown(d: { title: string; body: string }) {
    const slug =
      d.title
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "") || "document";
    const blob = new Blob([d.body], { type: "text/markdown" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${slug}.md`;
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="proj-shell-content docs-page">
      <aside className="docs-sidebar">
        <div className="docs-new">
          <select
            className="select"
            value={newKind}
            onChange={(e) => setNewKind(e.target.value as DocKind)}
            aria-label="New document kind"
          >
            {DOC_KINDS.map((k) => (
              <option key={k.value} value={k.value}>
                {k.label}
              </option>
            ))}
          </select>
          <Button
            variant="primary"
            onClick={() => create.mutate()}
            disabled={create.isPending}
          >
            New
          </Button>
        </div>
        <div className="docs-filter">
          <button
            type="button"
            className={`chip-btn${filter === "all" ? " on" : ""}`}
            onClick={() => setFilter("all")}
          >
            All
          </button>
          {DOC_KINDS.map((k) => (
            <button
              key={k.value}
              type="button"
              className={`chip-btn${filter === k.value ? " on" : ""}`}
              onClick={() => setFilter(k.value)}
            >
              {k.label}
            </button>
          ))}
        </div>
        {filter === "decision" && (
          <div className="docs-subfilter">
            <select
              className="select"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              aria-label="Filter by status"
            >
              <option value="all">Any status</option>
              <option value="proposed">Proposed</option>
              <option value="accepted">Accepted</option>
              <option value="superseded">Superseded</option>
            </select>
            <select
              className="select"
              value={ownerFilter}
              onChange={(e) => setOwnerFilter(e.target.value)}
              aria-label="Filter by owner"
            >
              <option value="all">Any owner</option>
              {decisionOwners.map((id) => (
                <option key={id} value={id}>
                  {memberName(id)}
                </option>
              ))}
            </select>
          </div>
        )}
        {list.isLoading ? (
          <Skeleton count={5} height={30} />
        ) : filtered.length === 0 ? (
          <p className="t-sm t-muted" style={{ padding: "8px 4px" }}>
            No documents yet.
          </p>
        ) : (
          <ul className="docs-list">
            {filtered.map((d) => (
              <li key={d.id}>
                <button
                  type="button"
                  className={`docs-item${selectedId === d.id ? " on" : ""}`}
                  onClick={() => setSelectedId(d.id)}
                >
                  <span className="docs-kind">{kindLabel(d.kind)}</span>
                  <span className="docs-title">{d.title}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </aside>

      <section className="docs-main">
        {!selected ? (
          <EmptyState
            icon="clip"
            title="Select or create a document"
            caption="Docs, meeting notes, decisions and notes for this project live here."
          />
        ) : editing ? (
          <div className="docs-editor">
            <input
              className="input docs-title-input"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              aria-label="Document title"
            />
            <RichTextEditor
              value={body}
              onChange={setBody}
              ariaLabel="Document body"
              upload={{ projectId }}
              minRows={18}
              placeholder="Write the document… paste an image to embed it"
            />
            <div className="docs-actions">
              <Button
                variant="primary"
                onClick={() => save.mutate()}
                disabled={save.isPending || !title.trim()}
              >
                {save.isPending ? "Saving…" : "Save"}
              </Button>
              <Button
                variant="ghost"
                onClick={() => {
                  setTitle(selected.title);
                  setBody(selected.body);
                  setEditing(false);
                }}
              >
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          <div className="docs-view">
            <div className="docs-view-head">
              <div>
                <span className="docs-kind">{kindLabel(selected.kind)}</span>
                <h2 className="docs-h">
                  {selected.kind === "adr" && selected.adrNumber != null
                    ? `ADR-${String(selected.adrNumber).padStart(3, "0")}: `
                    : ""}
                  {selected.title}
                </h2>
              </div>
              <div className="docs-actions">
                <Button variant="secondary" onClick={() => setEditing(true)}>
                  Edit
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => exportMarkdown(selected)}
                >
                  Export .md
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => remove.mutate(selected.id)}
                >
                  Delete
                </Button>
              </div>
            </div>
            {selected.kind === "adr" && (
              <AdrMeta
                doc={selected}
                allDocs={docs}
                onStatus={(status) => adrPatch.mutate({ status })}
                onSupersedes={(supersedesId) =>
                  adrPatch.mutate({ supersedesId })
                }
                onSelectDoc={setSelectedId}
              />
            )}
            {selected.kind === "meeting" && (
              <MeetingActions
                projectId={projectId}
                note={selected}
                onCreated={() =>
                  qc.invalidateQueries({
                    queryKey: queryKeys.tasksFor(projectId),
                  })
                }
              />
            )}
            {selected.kind === "decision" && (
              <div className="adr-meta">
                <div className="adr-meta-row">
                  <span className="t-sm t-muted">Status</span>
                  <span
                    className={`adr-status adr-status-${selected.status ?? "proposed"}`}
                  >
                    {selected.status ?? "proposed"}
                  </span>
                  <select
                    className="select"
                    value={selected.status ?? "proposed"}
                    onChange={(e) =>
                      adrPatch.mutate({
                        status: e.target.value as
                          | "proposed"
                          | "accepted"
                          | "superseded",
                      })
                    }
                    aria-label="Decision status"
                    style={{ width: 150 }}
                  >
                    <option value="proposed">proposed</option>
                    <option value="accepted">accepted</option>
                    <option value="superseded">superseded</option>
                  </select>
                </div>
                <div className="adr-meta-row">
                  <span className="t-sm t-muted">Owner</span>
                  <span className="t-sm">{memberName(selected.createdBy)}</span>
                  <span className="t-sm t-muted" style={{ marginLeft: 12 }}>
                    Decided
                  </span>
                  <span className="t-sm">
                    {new Date(selected.createdAt).toLocaleDateString()}
                  </span>
                </div>
              </div>
            )}
            <div className="adr-meta-row" style={{ marginBottom: 4 }}>
              <span className="t-sm t-muted">Linked task</span>
              <select
                className="select"
                value={selected.taskId ?? ""}
                onChange={(e) => linkTask.mutate(e.target.value || null)}
                disabled={linkTask.isPending}
                aria-label="Linked task"
                style={{ width: 280, maxWidth: "100%" }}
              >
                <option value="">— none —</option>
                {(tasksQ.data?.tasks ?? [])
                  .filter((t) => t.parentTaskId == null)
                  .map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.title}
                    </option>
                  ))}
              </select>
              {selected.taskId && (
                <Link
                  to="/tasks/$taskId"
                  params={{ taskId: selected.taskId }}
                  className="adr-link"
                >
                  Open task →
                </Link>
              )}
            </div>
            <MarkdownView source={selected.body || "_Empty document._"} />
            <DocHistory
              docId={selected.id}
              updatedAt={selected.updatedAt}
              onRestore={(v) => restore.mutate(v)}
              restoring={restore.isPending}
            />
          </div>
        )}
      </section>
    </div>
  );
}

/** Collapsible edit-history for a document: prior title/body snapshots. */
function DocHistory({
  docId,
  updatedAt,
  onRestore,
  restoring,
}: {
  docId: string;
  updatedAt: string;
  onRestore: (v: { title: string; body: string }) => void;
  restoring: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [preview, setPreview] = useState<string | null>(null);
  const q = useQuery({
    queryKey: ["documents", docId, "versions", updatedAt],
    queryFn: () => api.documentVersions(docId),
    enabled: open,
  });
  const versions = q.data?.versions ?? [];
  return (
    <div className="doc-history">
      <button
        type="button"
        className="chip-btn"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
      >
        {open ? "Hide history" : "History"}
        {versions.length > 0 ? ` (${versions.length})` : ""}
      </button>
      {open && (
        <div style={{ marginTop: 8 }}>
          {q.isLoading ? (
            <p className="t-sm t-muted">Loading…</p>
          ) : versions.length === 0 ? (
            <p className="t-sm t-muted">No prior versions yet.</p>
          ) : (
            <ul className="doc-history-list">
              {versions.map((v) => (
                <li key={v.id} className="adr-meta-row">
                  <span className="t-sm" style={{ flex: 1 }}>
                    <strong>{v.title}</strong> · {v.editedByName ?? "unknown"} ·{" "}
                    {new Date(v.editedAt).toLocaleString()}
                  </span>
                  <button
                    type="button"
                    className="adr-link"
                    onClick={() => setPreview(preview === v.id ? null : v.id)}
                  >
                    {preview === v.id ? "Hide" : "View"}
                  </button>
                  <button
                    type="button"
                    className="adr-link"
                    disabled={restoring}
                    onClick={() => {
                      if (
                        window.confirm(
                          "Restore this version? The current content is saved to history first.",
                        )
                      )
                        onRestore({ title: v.title, body: v.body });
                    }}
                  >
                    Restore
                  </button>
                </li>
              ))}
            </ul>
          )}
          {preview && (
            <div
              className="doc-history-preview"
              style={{
                marginTop: 8,
                padding: 12,
                border: "1px solid var(--border)",
                borderRadius: 8,
              }}
            >
              <MarkdownView
                source={
                  versions.find((v) => v.id === preview)?.body ||
                  "_Empty version._"
                }
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Meeting-note action items → tasks. Each unchecked `- [ ]` line becomes a
 * one-click task creation; the new task's description back-links to the note.
 */
function MeetingActions({
  projectId,
  note,
  onCreated,
}: {
  projectId: string;
  note: DocumentRow;
  onCreated: () => void;
}) {
  const items = parseActionItems(note.body);
  const [done, setDone] = useState<Record<string, string>>({});
  const create = useMutation({
    mutationFn: (text: string) =>
      api.createTask(projectId, {
        title: text,
        description: `From meeting note [${note.title}](/projects/${projectId}/docs?doc=${note.id}).`,
      }),
    onSuccess: (r, text) => {
      setDone((s) => ({ ...s, [text]: r.task.id }));
      onCreated();
    },
  });
  if (items.length === 0) return null;
  return (
    <div className="adr-meta">
      <div className="t-sm t-muted">Action items</div>
      {items.map((text, i) => (
        <div key={i} className="adr-meta-row">
          <span style={{ flex: 1 }}>{text}</span>
          {done[text] ? (
            <span className="t-sm t-muted">✓ task created</span>
          ) : (
            <Button
              variant="secondary"
              onClick={() => create.mutate(text)}
              disabled={create.isPending}
            >
              Create task
            </Button>
          )}
        </div>
      ))}
    </div>
  );
}

const ADR_STATUS: ("proposed" | "accepted" | "superseded")[] = [
  "proposed",
  "accepted",
  "superseded",
];

function adrLabel(d: { adrNumber?: number | null; title: string }): string {
  const n =
    d.adrNumber != null ? `ADR-${String(d.adrNumber).padStart(3, "0")}` : "ADR";
  return `${n}: ${d.title}`;
}

/** ADR-specific metadata: status lifecycle + supersede links (both directions). */
function AdrMeta({
  doc,
  allDocs,
  onStatus,
  onSupersedes,
  onSelectDoc,
}: {
  doc: DocumentRow;
  allDocs: DocumentRow[];
  onStatus: (s: "proposed" | "accepted" | "superseded") => void;
  onSupersedes: (id: string | null) => void;
  onSelectDoc: (id: string) => void;
}) {
  const otherAdrs = allDocs.filter((d) => d.kind === "adr" && d.id !== doc.id);
  const supersedes = doc.supersedesId
    ? (allDocs.find((d) => d.id === doc.supersedesId) ?? null)
    : null;
  // The ADR (if any) that supersedes THIS one — the reverse link.
  const supersededBy = allDocs.find(
    (d) => d.kind === "adr" && d.supersedesId === doc.id,
  );
  return (
    <div className="adr-meta">
      <label className="adr-meta-row">
        <span className="t-sm t-muted">Status</span>
        <span className={`adr-status adr-status-${doc.status ?? "proposed"}`}>
          {doc.status ?? "proposed"}
        </span>
        <select
          className="select"
          value={doc.status ?? "proposed"}
          onChange={(e) =>
            onStatus(e.target.value as "proposed" | "accepted" | "superseded")
          }
          aria-label="ADR status"
          style={{ width: 150 }}
        >
          {ADR_STATUS.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
      </label>
      <label className="adr-meta-row">
        <span className="t-sm t-muted">Supersedes</span>
        <select
          className="select"
          value={doc.supersedesId ?? ""}
          onChange={(e) => onSupersedes(e.target.value || null)}
          aria-label="Supersedes ADR"
          style={{ width: 280 }}
        >
          <option value="">— none —</option>
          {otherAdrs.map((d) => (
            <option key={d.id} value={d.id}>
              {adrLabel(d)}
            </option>
          ))}
        </select>
        {supersedes && (
          <button
            type="button"
            className="adr-link"
            onClick={() => onSelectDoc(supersedes.id)}
          >
            → {adrLabel(supersedes)}
          </button>
        )}
      </label>
      {supersededBy && (
        <div className="adr-meta-row">
          <span className="t-sm t-muted">Superseded by</span>
          <button
            type="button"
            className="adr-link"
            onClick={() => onSelectDoc(supersededBy.id)}
          >
            {adrLabel(supersededBy)}
          </button>
        </div>
      )}
    </div>
  );
}

export const Route = createFileRoute("/projects/$projectId/docs")({
  validateSearch: (search: Record<string, unknown>): { doc?: string } => ({
    doc: typeof search.doc === "string" ? search.doc : undefined,
  }),
  component: DocsView,
});
